// @ts-strict-ignore
import { Box } from "@saleor/macaw-ui-next";
import React from "react";
import { useIntl } from "react-intl";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";

import { analyticsMessages } from "../../messages";
import { EmptyState } from "../EmptyState";
import { seriesColors, surfaceCardStyle } from "./tokens";

interface DonutChartTileV2Props<T extends Record<string, any> = Record<string, any>> {
  title: string;
  description?: string;
  data: T[];
  labelKey: keyof T & string;
  valueKey: keyof T & string;
  loading?: boolean;
  na?: boolean;
  emptyMessage?: string;
  valueFormatter?: (value: number) => string;
  centerLabel?: string;
  height?: number;
  /** Optional callback fired with the slice's label string when clicked. */
  onSliceClick?: (label: string) => void;
}

export const DonutChartTile: React.FC<DonutChartTileV2Props> = ({
  title,
  description,
  data,
  labelKey,
  valueKey,
  loading,
  na,
  emptyMessage,
  valueFormatter,
  centerLabel,
  height = 280,
  onSliceClick,
}) => {
  const intl = useIntl();

  const items = React.useMemo(
    () =>
      (data || []).map((d, i) => ({
        label: String(d[labelKey]),
        value: Number(d[valueKey] as any) || 0,
        color: seriesColors[i % seriesColors.length],
      })),
    [data, labelKey, valueKey],
  );

  const total = React.useMemo(() => items.reduce((s, it) => s + it.value, 0), [items]);
  const empty = !na && !loading && (!data || data.length === 0);

  return (
    <Box padding={5} style={{ ...surfaceCardStyle }}>
      <Box marginBottom={4}>
        <Box as="div" fontSize={2} color="default2">
          {title}
        </Box>
        {description && (
          <Box as="div" fontSize={1} color="default2" marginTop={1} style={{ opacity: 0.8 }}>
            {description}
          </Box>
        )}
      </Box>
      <Box display="flex" alignItems="center" gap={5} style={{ height }}>
        {na ? (
          <Box width="100%" textAlign="center">{intl.formatMessage(analyticsMessages.commonNA)}</Box>
        ) : loading ? (
          <Box width="100%" textAlign="center">{intl.formatMessage(analyticsMessages.commonLoading)}</Box>
        ) : empty ? (
          <EmptyState message={emptyMessage ?? intl.formatMessage(analyticsMessages.commonNoData)} compact />
        ) : (
          <>
            <Box position="relative" __width={180} __height={180} style={{ flexShrink: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={items}
                    dataKey="value"
                    nameKey="label"
                    innerRadius={55}
                    outerRadius={80}
                    paddingAngle={2}
                    stroke="#fff"
                    strokeWidth={2}
                    onClick={onSliceClick ? (slice: any) => onSliceClick(slice?.payload?.label || slice?.name) : undefined}
                    style={onSliceClick ? { cursor: "pointer" } : undefined}
                  >
                    {items.map((it, index) => (
                      <Cell key={`cell-${index}`} fill={it.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      borderRadius: 8,
                      border: "none",
                      background: "rgba(17, 24, 39, 0.95)",
                      color: "#fff",
                      fontSize: 12,
                    }}
                    formatter={(v: number) => (valueFormatter ? valueFormatter(v) : v)}
                  />
                </PieChart>
              </ResponsiveContainer>
              <Box
                position="absolute"
                style={{
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  textAlign: "center",
                  pointerEvents: "none",
                }}
              >
                <Box as="div" fontSize={1} color="default2">
                  {centerLabel ?? "Total"}
                </Box>
                <Box
                  as="div"
                  fontSize={5}
                  fontWeight="bold"
                  style={{ letterSpacing: "-0.01em", fontFeatureSettings: '"tnum"' }}
                >
                  {valueFormatter ? valueFormatter(total) : total}
                </Box>
              </Box>
            </Box>

            <Box
              display="flex"
              flexDirection="column"
              gap={2}
              style={{ flex: 1, minWidth: 0, overflow: "auto", maxHeight: height - 20 }}
            >
              {items.map(item => {
                const pct = total > 0 ? (item.value / total) * 100 : 0;

                return (
                  <Box
                    key={item.label}
                    onClick={onSliceClick ? () => onSliceClick(item.label) : undefined}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={3}
                    paddingY={1}
                    style={{ cursor: onSliceClick ? "pointer" : "default" }}
                  >
                    <Box display="flex" alignItems="center" gap={2} style={{ minWidth: 0, flex: 1 }}>
                      <Box
                        as="span"
                        __width={10}
                        __height={10}
                        borderRadius={1}
                        style={{ background: item.color, flexShrink: 0 }}
                      />
                      <Box
                        as="span"
                        fontSize={2}
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.label}
                      </Box>
                    </Box>
                    <Box display="flex" alignItems="center" gap={3} style={{ flexShrink: 0 }}>
                      <Box
                        as="span"
                        fontSize={2}
                        fontWeight="bold"
                        style={{ fontFeatureSettings: '"tnum"' }}
                      >
                        {valueFormatter ? valueFormatter(item.value) : item.value}
                      </Box>
                      <Box
                        as="span"
                        fontSize={1}
                        color="default2"
                        style={{ minWidth: 36, textAlign: "right" }}
                      >
                        {pct.toFixed(1)}%
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};
